import { Button, Input, Modal, Popconfirm, Select } from "antd"
import { useAttachmentFolders, useAttachmentTags, useDeleteAttachmentMutation, useUpdateAttachmentMutation } from "./attachmentsQueries"
import { Attachment, UpdateAttachmentData, joinTags, splitTags } from "./attachmentsModel"
import VSpace from "../common/VSpace"
import { useEffect, useState } from "react"

export type Props = {
  isOpen: boolean
  onClose: () => void
  attachment: Attachment
  customerId: number
  propertyId?: number
}

const labelStyle: React.CSSProperties = {
  width: '100%',
  fontWeight: 'initial'
}

const EditAttachmentModal = ({ isOpen, onClose, attachment, customerId, propertyId }: Props) => {
  const foldersQuery = useAttachmentFolders(customerId, propertyId, false)
  const [data, setData] = useState<UpdateAttachmentData>(attachment)
  const tagsQuery = useAttachmentTags(isOpen)
  const updateMutation = useUpdateAttachmentMutation(onClose)
  const deleteMutation = useDeleteAttachmentMutation(onClose)

  useEffect(() => {
    setData({
      id: attachment.id,
      name: attachment.name,
      tags: attachment.tags,
      folder: attachment.folder,
      jobId: attachment.jobId
    })
  }, [attachment])

  const folderOptions = (foldersQuery.data ?? [])
    .map(f => ({ label: f.folder, value: f.folder }))
  const tagOptions = (tagsQuery.data ?? []).map(t => ({ label: t, value: t }))

  const canUpdate = data.name && data.folder
  const isMutating = updateMutation.isLoading || deleteMutation.isLoading

  const handleFolderChange = (folder: string) => {
    const updatedData = { ...data, folder }
    const jobId = foldersQuery.data?.find(x => x.folder === folder)?.jobId
    if (jobId) {
      updatedData.jobId = jobId
    }
    setData(updatedData)
  }

  return <Modal
    title="Edit Attachment"
    open={isOpen}
    onCancel={onClose}
    onOk={() => updateMutation.mutate(data)}
    okText="Update"
    okButtonProps={{ disabled: !canUpdate || isMutating, loading: updateMutation.isLoading }}
    footer={(_, {OkBtn, CancelBtn}) => <>
        <CancelBtn />
        <Popconfirm title="Are you sure you want to delete this attachment?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => deleteMutation.mutate(attachment.id)}
        >
          <Button type="primary" danger loading={deleteMutation.isLoading} disabled={deleteMutation.isLoading}>Delete</Button>
        </Popconfirm>
        <OkBtn />
      </>
    }
  >
    <VSpace>
      <label style={labelStyle}>
        File name
        <Input
          value={data.name}
          onChange={e => setData({...data, name: e.target.value ?? ''})}
          placeholder={"attachment name"}
          maxLength={150}
          status={data.name ? undefined : 'error'}
        />
      </label>
      <label style={labelStyle}>
        Folder
        <Select
          value={data.folder}
          onChange={handleFolderChange}
          options={folderOptions}
          style={{ width: '100%' }}
        />
      </label>

      <label style={labelStyle}>
        Tags
        <Select
          mode="tags"
          value={splitTags(data.tags)}
          onChange={tags => setData({ ...data, tags: joinTags(tags) })}
          options={tagOptions}
          style={{ width: '100%' }}
        />
      </label>
    </VSpace>
  </Modal>
}

export default EditAttachmentModal
